import React from 'react'
import { Teacher } from '../providers/context'
import { ThreePanelLayout } from '../layouts/threePanel'
import { ControlledModal, ModalTitle } from '../layouts/components/modal'
import { ExportWidget } from './dashboard/widgets'
import { LocalTable } from '../components/table'
import { ColumnDef } from '@tanstack/react-table'
import { LineChart, LineChartData } from '../components/charts/lineChart'
import { useRequest } from '../request'
import { Loader } from '../components/simple/loader'
import { useParams } from 'react-router-dom'
import { DateWrapper } from '../components/simple/date'

type AbsenceDetail = {
  id: number
  message_id: number
  student_id: number
  replied_by_id: number
  class_id: number
  comment: string
  excused: number
  resolved: number
  updated_at: string // ISO date string
  created_at: string // ISO date string
  attachment: string | null
  class_name: string
  fname: string
  lname: string
  grade: string
}

type AbsencesPerClass = {
  class_name: string
  excused: number
  absences: number
}

type AbsenceDay = {
  date: string
  classes: string[]
  excused: number
  absences: AbsenceDetail[]
}

type Student = {
  id: number
  sync_id: string
  name: string
  grade: string
}

type AbsenceReport = {
  totalExcusedDays: number
  totalAbsences: number
  absencesPerClass: Record<string, AbsencesPerClass>
  absences: Record<string, AbsenceDay>
  student: Student
}

export const StudentAttendanceStatsPage: React.FC<{ user: Teacher }> = ({ user }) => {
  const { studentID } = useParams()
  const [showExportModal, setShowExportModal] = React.useState(false)

  const { data, loading } = useRequest<AbsenceReport>(
    `/stats/frontend_api_absenceStatsInd?id=${studentID}`,
    undefined,
    true,
    'GET'
  )

  const chartData: LineChartData | undefined = React.useMemo(() => {
    if (!data) return

    const chartData: LineChartData = {
      labels: [],
      datasets: [
        {
          label: 'Excused Absences',
          color: '#4F8A8B',
          data: [],
        },
        {
          label: 'Total Absences',
          color: '#F45B69',
          data: [],
        },
      ],
    }

    const dates = Object.keys(data.absences).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

    dates.forEach((date) => {
      const absence = data.absences[date]
      chartData.labels.push(date)
      chartData.datasets[0].data.push(absence.excused)
      chartData.datasets[1].data.push(absence.absences.length)
    })

    return chartData
  }, [data])

  // Group absences by date
  const tableData = React.useMemo(() => {
    if (!data) return []

    // Create an array of objects: { date, absences: AbsenceDetail[] }
    return Object.entries(data.absences)
      .map(([date, absenceDay]) => ({
        date,
        absencesCount: absenceDay.absences.length,
        classes: absenceDay.absences.map((absence) => absence.class_name),
        excused: absenceDay.excused,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }, [data])

  // Columns for grouped data: show date as a group header, then absences
  const columns: ColumnDef<{ date: string; absencesCount: number; classes: string[]; excused: number }>[] =
    React.useMemo(
      () => [
        {
          accessorKey: 'date',
          header: 'Date',
          cell: ({ row }) => <>{row.original.date ? <DateWrapper date={row.original.date} /> : 'unknown'}</>,
        },
        {
          accessorKey: 'absencesCount',
          header: '# Absences',
          cell: (info) => info.getValue(),
        },
        {
          accessorKey: 'excused',
          header: 'Excused',
          cell: (info) => info.getValue(),
        },
        {
          accessorKey: 'classes',
          header: 'Classes',
          cell: (info) => (
            <div>
              {(info.getValue() as string[]).map((name) => (
                <div key={name} className="flex gap-4 border-b py-1 last:border-b-0">
                  <span className="w-32">{name}</span>
                </div>
              ))}
            </div>
          ),
        },
      ],
      [data]
    )

  const mostFrequentClassTableData = React.useMemo(() => {
    if (!data) return []

    // Count frequency of each class_name in all unexcused absences
    const classFrequency: Record<string, number> = {}

    Object.values(data.absences).forEach((absenceDay) => {
      absenceDay.absences.forEach((absence) => {
        if (!absence.excused) {
          classFrequency[absence.class_name] = (classFrequency[absence.class_name] || 0) + 1
        }
      })
    })

    // Find the class_name(s) with the highest frequency
    const maxCount = Math.max(0, ...Object.values(classFrequency))
    const mostFrequentClasses = Object.entries(classFrequency)
      .filter(([_, count]) => count === maxCount)
      .map(([class_name, count]) => ({ class_name, count }))

    return mostFrequentClasses
  }, [data])

  const mostFrequentClassColumns: ColumnDef<{ class_name: string; count: number }>[] = React.useMemo(
    () => [
      {
        accessorKey: 'class_name',
        header: 'Class Name',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'count',
        header: 'Count',
        cell: (info) => info.getValue(),
      },
    ],
    []
  )

  return (
    <ThreePanelLayout
      noMobile
      user={user}
      left={<></>}
      floating={
        <ControlledModal
          noAutoFocus
          open={showExportModal}
          onOpenChange={(o) => !o && setShowExportModal(false)}
          className="!bg-charcoal"
          size="xs"
        >
          <ModalTitle hidden>Export Absences</ModalTitle>
          <ExportWidget className="!shadow-none" />
        </ControlledModal>
      }
    >
      <div className="flex flex-grow flex-col gap-4 overflow-auto bg-lightBlue p-4">
        <div className={`flex flex-col gap-2 rounded bg-white p-4 shadow ${loading && data ? 'opacity-50' : ''}`}>
          {data ? <b className="ml-4">Absence Stats for {data?.student.name}</b> : null}
          {chartData ? <LineChart className="min-h-72 px-2" data={chartData} legend /> : <Loader className="my-4" />}
        </div>
        <div
          className={`flex flex-col gap-2 rounded bg-white shadow ${loading && mostFrequentClassTableData ? 'opacity-50' : ''}`}
        >
          {mostFrequentClassTableData ? <b className="ml-4">Classes with most unexcused absences</b> : null}
          {mostFrequentClassTableData ? (
            <LocalTable
              className="text-sm"
              columns={mostFrequentClassColumns}
              data={mostFrequentClassTableData}
              pagination={0}
            />
          ) : (
            <Loader className="my-4" />
          )}
        </div>
        <div className={`flex flex-col gap-2 rounded bg-white p-4 shadow ${loading && tableData ? 'opacity-50' : ''}`}>
          {tableData ? <b className="ml-4">All absences</b> : null}
          {tableData ? (
            <LocalTable
              className="text-sm"
              columns={columns}
              data={tableData}
              pagination={10}
              exportName={`absencesForStudent-${data?.student.name}`}
            />
          ) : (
            <Loader className="my-4" />
          )}
        </div>
      </div>
    </ThreePanelLayout>
  )
}
